import { useMemo } from 'react';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Button } from 'components/Button/Button';
import { DropdownChevron } from 'components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { closeDropdownFromElementWithin } from 'components/Dropdown/closeDropdownFromElementWithin';
import { DropdownListMenu } from 'components/Dropdown/menus/ListMenu/DropdownListMenu';
import { Hide } from 'components/Hide';
import { NotificationList } from 'components/NotificationList/NotificationList';
import { NotificationsDropdownIcon } from 'components/NotificationsDropdown/Icon/NotificationsDropdownIcon';
import { H4 } from 'components/Text/H4';
import { colors } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';

import {
  MobileNotificationsDropdownHeader,
  MobileNotificationsDropdownHolder,
} from './NotificationsDropdown.styled';

type Props = {
  notifications: (ApiNotification | MappedNotification)[];
  onToggle: (open: boolean) => void;
};

export function NotificationsDropdown({ notifications, onToggle }: Props) {
  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  return (
    <Dropdown
      trigger={
        <DropdownHeaderTrigger
          variant="grey"
          qaId="notifications-dropdown-toggle"
          aria-label={getText('Notifications')}
        >
          <NotificationsDropdownIcon
            unseenNotificationsCount={unseenNotifications.length}
          />

          <Hide xs sm md>
            <DropdownChevron />
          </Hide>
        </DropdownHeaderTrigger>
      }
      onToggle={onToggle}
    >
      <MobileNotificationsDropdownHolder data-qa-id="mobile-notifications-menu">
        <MobileNotificationsDropdownHeader>
          <H4 mb={0} color={colors.brandBlue}>
            {getText('Notifications')}
          </H4>

          <Button
            data-qa-id="close-mobile-notifications"
            onClick={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            aria-label={getText('Close notifications')}
            variant="tertiary"
            noPadding
            icon={{ type: 'normalized', name: 'close' }}
          />
        </MobileNotificationsDropdownHeader>

        <NotificationList
          notifications={notifications}
          onClick={(event) =>
            closeDropdownFromElementWithin(event.target as HTMLElement)
          }
        />
      </MobileNotificationsDropdownHolder>

      <DropdownListMenu
        height="370px"
        width={260}
        qaId="notifications-dropdown"
      >
        <NotificationList notifications={notifications} dismissible />
      </DropdownListMenu>
    </Dropdown>
  );
}
